import { Suspense, lazy, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import type { Contact } from "@/api/queries";
import { BLANK } from "./composeSeed";
import { useMailbox } from "./queries";

/**
 * *Enviar correo*, beside an address: opens the composer with it already in
 * *Para*. Nothing is sent from here — the composer is the one the mail screen
 * uses, drafts, signature and all, and the send lands on this contact's
 * timeline the way every other one does (D33).
 *
 * Three reasons it can be off, and each is said rather than left as a dead
 * button: the seller has no mailbox connected, or one that needs reconnecting,
 * which the server would refuse at the send; the mailbox could not be read at
 * all; and the contact objected or is blocked, who nobody writes to on any
 * channel (D36).
 */
const MailComposer = lazy(() =>
  import("./MailComposer").then((module) => ({ default: module.MailComposer })),
);

export function SendMailButton({
  contact,
  address,
  compact = false,
}: {
  contact: Contact;
  address: string;
  /** On a contact card, where the reason is a few words and the rest a hover. */
  compact?: boolean;
}) {
  const { t } = useTranslation();
  const [composing, setComposing] = useState(false);
  const mailbox = useMailbox();

  const opposed = Boolean(contact.objected_at || contact.blocked_at);
  const connected = mailbox.data?.status === "connected";
  // Where the fix is one click away, the hint is the link to it.
  const fixable = !opposed && !connected && !mailbox.isPending && !mailbox.isError;
  const reason = opposed
    ? t("mail.write.opposed")
    : connected || mailbox.isPending
      ? undefined
      : mailbox.isError
        ? t("errors.mailbox")
        : mailbox.data
          ? t("mail.write.reconnect")
          : t("mail.write.notConnected");
  const short = opposed
    ? t("mail.write.opposedShort")
    : fixable
      ? t("mail.write.notConnectedShort")
      : reason;

  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      <button
        type="button"
        className="btn btn-secondary px-3 py-1.5 text-sm"
        disabled={opposed || !connected}
        title={reason}
        onClick={() => setComposing(true)}
      >
        {t("mail.write.button")}
      </button>
      {reason === undefined ? null : compact ? (
        fixable ? (
          <Link
            className="text-[11px] text-mute-600 underline"
            to="/ajustes"
            title={reason}
          >
            {short}
          </Link>
        ) : (
          <span className="text-[11px] text-mute-600" title={reason}>
            {short}
          </span>
        )
      ) : (
        <span className="text-[11px] text-mute-600">
          {reason}{" "}
          {fixable ? (
            <Link className="underline" to="/ajustes">
              {t("mail.notConnected.cta")}
            </Link>
          ) : null}
        </span>
      )}
      {composing ? (
        <Suspense fallback={null}>
          <MailComposer
            seed={{ ...BLANK, to: address }}
            onClose={() => setComposing(false)}
          />
        </Suspense>
      ) : null}
    </span>
  );
}
